import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { z } from 'zod'
import { formatFullDateTime } from './format'
import {
  normalizeTreeSelection,
  toggleTreeSelection,
  treeCheckState,
  type TreeCheckState,
  type TreeNodeData,
} from './tree'

dayjs.extend(utc)

const cnOffset = 8

function atCN(value: Date | string | number) {
  return dayjs(value).utcOffset(cnOffset)
}

export const voucherScopeKinds = [
  'all',
  'tickets',
  'category',
  'product',
  'sku',
] as const

export const voucherValidityUnits = ['day', 'week', 'month', 'year'] as const

export const voucherValidityKinds = ['none', 'relative', 'absolute'] as const

/** 小程序首页核心票券下列最多展示的主要票券种类 */
export const MAIN_VOUCHER_HOME_LIMIT = 3

export type VoucherScopeKind = (typeof voucherScopeKinds)[number]
export type VoucherValidityUnit = (typeof voucherValidityUnits)[number]
export type VoucherValidityKind = (typeof voucherValidityKinds)[number]

export type VoucherScope =
  | { kind: 'all' }
  | { kind: 'tickets' }
  | { kind: 'category'; id: string }
  | { kind: 'product'; id: string }
  | { kind: 'sku'; id: string }

export type VoucherValidity =
  | { kind: 'relative'; amount: number; unit: VoucherValidityUnit }
  | { kind: 'absolute'; expiresAt: string }

export interface VoucherValidityColumns {
  validityRelativeAmount: number | null
  validityRelativeUnit: VoucherValidityUnit | null
  validityAbsoluteAt: Date | null
}

export interface VoucherScopeTreeNode {
  scope: VoucherScope
  label: string
  pathLabel: string
  children: VoucherScopeTreeNode[]
}

export interface VoucherScopeCatalogTicket {
  productId: string
  name: string
  skus: Array<{ id: string; name: string }>
}

export interface VoucherScopeCatalogCategory {
  id: string
  name: string
  products: Array<{
    id: string
    name: string
    skus: Array<{ id: string; name: string }>
  }>
}

export interface VoucherSkuTarget {
  skuId: string
  productId: string
  categoryId: string | null
  productKind: 'ticket' | 'goods'
}

export const voucherScopeKindLabels = {
  all: '全部',
  tickets: '赛事',
  category: '分类',
  product: '商品',
  sku: '规格',
} as const satisfies Record<VoucherScopeKind, string>

export const voucherValidityUnitLabels = {
  day: '天',
  week: '周',
  month: '月',
  year: '年',
} as const satisfies Record<VoucherValidityUnit, string>

export const voucherValidityKindLabels = {
  none: '不设置',
  relative: '相对有效期',
  absolute: '固定有效期',
} as const satisfies Record<VoucherValidityKind, string>

const localDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/

export function voucherScopeFromRow(row: {
  kind: VoucherScopeKind
  categoryId: string | null
  productId: string | null
  productSkuId: string | null
}): VoucherScope {
  if (row.kind === 'all') return { kind: 'all' }
  if (row.kind === 'tickets') return { kind: 'tickets' }
  if (row.kind === 'category' && row.categoryId)
    return { kind: 'category', id: row.categoryId }
  if (row.kind === 'product' && row.productId)
    return { kind: 'product', id: row.productId }
  if (row.kind === 'sku' && row.productSkuId)
    return { kind: 'sku', id: row.productSkuId }
  throw new RangeError('适用范围节点不完整')
}

export function voucherScopeToRow(scope: VoucherScope) {
  return {
    kind: scope.kind,
    categoryId: scope.kind === 'category' ? scope.id : null,
    productId: scope.kind === 'product' ? scope.id : null,
    productSkuId: scope.kind === 'sku' ? scope.id : null,
  }
}

export function voucherScopeKey(scope: VoucherScope): string {
  if (scope.kind === 'all' || scope.kind === 'tickets') return scope.kind
  return `${scope.kind}:${scope.id}`
}

export function parseVoucherScopeKey(key: string): VoucherScope | null {
  if (key === 'all' || key === 'tickets') return { kind: key }
  const match = /^(category|product|sku):(.+)$/.exec(key)
  if (!match) return null
  return { kind: match[1] as 'category' | 'product' | 'sku', id: match[2]! }
}

export const voucherScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('all') }),
  z.object({ kind: z.literal('tickets') }),
  z.object({
    kind: z.literal('category'),
    id: z.string().uuid('适用范围不合法'),
  }),
  z.object({
    kind: z.literal('product'),
    id: z.string().uuid('适用范围不合法'),
  }),
  z.object({ kind: z.literal('sku'), id: z.string().uuid('适用范围不合法') }),
])

export function buildVoucherScopeTree(input: {
  tickets: VoucherScopeCatalogTicket[]
  categories: VoucherScopeCatalogCategory[]
}): VoucherScopeTreeNode[] {
  return [
    {
      scope: { kind: 'all' },
      label: voucherScopeKindLabels.all,
      pathLabel: voucherScopeKindLabels.all,
      children: [
        {
          scope: { kind: 'tickets' },
          label: voucherScopeKindLabels.tickets,
          pathLabel: voucherScopeKindLabels.tickets,
          children: input.tickets.map((ticket) => ({
            scope: { kind: 'product', id: ticket.productId },
            label: ticket.name,
            pathLabel: ticket.name,
            children: ticket.skus.map((sku) => ({
              scope: { kind: 'sku', id: sku.id },
              label: sku.name,
              pathLabel: `${ticket.name} · ${sku.name}`,
              children: [],
            })),
          })),
        },
        ...input.categories.map((category) => ({
          scope: { kind: 'category' as const, id: category.id },
          label: category.name,
          pathLabel: category.name,
          children: category.products.map((product) => ({
            scope: { kind: 'product' as const, id: product.id },
            label: product.name,
            pathLabel: `${category.name} · ${product.name}`,
            children: product.skus.map((sku) => ({
              scope: { kind: 'sku' as const, id: sku.id },
              label: sku.name,
              pathLabel: `${category.name} · ${product.name} · ${sku.name}`,
              children: [],
            })),
          })),
        })),
      ],
    },
  ]
}

export function voucherScopeTreeToData(
  nodes: VoucherScopeTreeNode[],
): TreeNodeData[] {
  return nodes.map((node) => ({
    id: voucherScopeKey(node.scope),
    label: node.label,
    pathLabel: node.pathLabel,
    children: voucherScopeTreeToData(node.children),
  }))
}

export function scopesToTreeIds(scopes: VoucherScope[]): string[] {
  return scopes.map(voucherScopeKey)
}

export function treeIdsToScopes(ids: string[]): VoucherScope[] {
  return ids.flatMap((id) => {
    const scope = parseVoucherScopeKey(id)
    return scope ? [scope] : []
  })
}

export function collectVoucherScopeKeys(
  nodes: VoucherScopeTreeNode[],
): Set<string> {
  const keys = new Set<string>()
  for (const node of nodes) {
    keys.add(voucherScopeKey(node.scope))
    for (const key of collectVoucherScopeKeys(node.children)) keys.add(key)
  }
  return keys
}

export function voucherScopeCheckState(
  selected: VoucherScope[],
  node: VoucherScopeTreeNode,
  ancestorSelected = false,
): TreeCheckState {
  return treeCheckState(
    selected.map(voucherScopeKey),
    {
      id: voucherScopeKey(node.scope),
      label: node.label,
      children: voucherScopeTreeToData(node.children),
    },
    ancestorSelected,
  )
}

export function normalizeVoucherScopes(
  selected: VoucherScope[],
  tree: VoucherScopeTreeNode[],
): VoucherScope[] {
  return treeIdsToScopes(
    normalizeTreeSelection(
      selected.map(voucherScopeKey),
      voucherScopeTreeToData(tree),
    ),
  )
}

export function toggleVoucherScope(
  selected: VoucherScope[],
  tree: VoucherScopeTreeNode[],
  target: VoucherScope,
): VoucherScope[] {
  return treeIdsToScopes(
    toggleTreeSelection(
      selected.map(voucherScopeKey),
      voucherScopeTreeToData(tree),
      voucherScopeKey(target),
    ),
  )
}

export function catalogAncestorIds(sku: VoucherSkuTarget): string[] {
  const ids = [`sku:${sku.skuId}`, `product:${sku.productId}`]
  if (sku.productKind === 'ticket') ids.push('tickets')
  else if (sku.categoryId) ids.push(`category:${sku.categoryId}`)
  ids.push('all')
  return ids
}

export function treeSelectionHitsSku(
  selected: string[],
  sku: VoucherSkuTarget,
): boolean {
  const ancestors = new Set(catalogAncestorIds(sku))
  return selected.some((id) => ancestors.has(id))
}

export function voucherScopeHitsSku(
  scope: VoucherScope,
  sku: VoucherSkuTarget,
): boolean {
  return treeSelectionHitsSku([voucherScopeKey(scope)], sku)
}

export function voucherScopesHitSku(
  scopes: VoucherScope[],
  sku: VoucherSkuTarget,
): boolean {
  return treeSelectionHitsSku(scopes.map(voucherScopeKey), sku)
}

export function formatVoucherScopeLabel(
  scope: VoucherScope,
  names: {
    categoryName?: string | null
    productName?: string | null
    skuName?: string | null
  },
): string {
  if (scope.kind === 'all') return voucherScopeKindLabels.all
  if (scope.kind === 'tickets') return voucherScopeKindLabels.tickets
  if (scope.kind === 'category') return names.categoryName ?? scope.id
  if (scope.kind === 'product') return names.productName ?? scope.id
  const productName = names.productName
  const skuName = names.skuName
  if (productName && skuName) return `${productName} · ${skuName}`
  return skuName ?? productName ?? scope.id
}

export function parseAbsoluteExpiresAt(value: string): Date {
  if (localDateTimePattern.test(value)) {
    return dayjs(`${value}:00+08:00`).toDate()
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) throw new RangeError('固定有效期不合法')
  return date
}

export function formatAbsoluteExpiresAtInput(value: Date | string): string {
  return atCN(value).format('YYYY-MM-DDTHH:mm')
}

export function voucherValidityFromColumns(
  columns: VoucherValidityColumns,
): VoucherValidity | null {
  if (
    columns.validityRelativeAmount != null &&
    columns.validityRelativeUnit != null
  ) {
    return {
      kind: 'relative',
      amount: columns.validityRelativeAmount,
      unit: columns.validityRelativeUnit,
    }
  }
  if (columns.validityAbsoluteAt) {
    return {
      kind: 'absolute',
      expiresAt: columns.validityAbsoluteAt.toISOString(),
    }
  }
  return null
}

export function voucherValidityToColumns(
  validity: VoucherValidity | null,
): VoucherValidityColumns {
  if (!validity) {
    return {
      validityRelativeAmount: null,
      validityRelativeUnit: null,
      validityAbsoluteAt: null,
    }
  }
  if (validity.kind === 'relative') {
    return {
      validityRelativeAmount: validity.amount,
      validityRelativeUnit: validity.unit,
      validityAbsoluteAt: null,
    }
  }
  return {
    validityRelativeAmount: null,
    validityRelativeUnit: null,
    validityAbsoluteAt: parseAbsoluteExpiresAt(validity.expiresAt),
  }
}

export function voucherValidityFromForm(input: {
  validityKind: VoucherValidityKind
  relativeAmount: number | null
  relativeUnit: VoucherValidityUnit | null
  absoluteExpiresAt: string | null
}): VoucherValidity | null {
  if (input.validityKind === 'relative') {
    if (input.relativeAmount == null || input.relativeUnit == null) return null
    return {
      kind: 'relative',
      amount: input.relativeAmount,
      unit: input.relativeUnit,
    }
  }
  if (input.validityKind === 'absolute') {
    if (!input.absoluteExpiresAt) return null
    return {
      kind: 'absolute',
      expiresAt: parseAbsoluteExpiresAt(input.absoluteExpiresAt).toISOString(),
    }
  }
  return null
}

export function voucherExpiresAt(
  validity: VoucherValidity | null,
  issuedAt: Date,
): Date | null {
  if (!validity) return null
  if (validity.kind === 'absolute') return parseAbsoluteExpiresAt(validity.expiresAt)
  return atCN(issuedAt).add(validity.amount, validity.unit).toDate()
}

export function formatVoucherValidity(
  validity: VoucherValidity | null,
): string {
  if (!validity) return voucherValidityKindLabels.none
  if (validity.kind === 'relative') {
    return `${validity.amount} ${voucherValidityUnitLabels[validity.unit]}`
  }
  return formatFullDateTime(parseAbsoluteExpiresAt(validity.expiresAt))
}
